import { useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

// Drawing-tool state machine for MapCanvas (Ruler/Burst/Cone/Line + GM-only Obstacle/Trigger
// Line/Wall/Door/Portal tools) — extracted verbatim (Phase 42 refactor), no behavior changes.
// Dialog-opening side effects (Obstacle/Door create dialogs) are delegated back to the caller
// via callbacks since those dialogs live outside this hook's concerns.
export function useMapDrawingTools({ game, scene, gridSize, arrivalPick, onArrivalPick, onOpenObstacleDialog, onOpenDoorDialog, onEmptyClick }) {
  const [activeTool, setActiveTool] = useState(null);
  const [ruler, setRuler] = useState(null);
  const [wallDraft, setWallDraft] = useState(null); // { start } while placing a wall
  const wallDraftRef = useRef(null); // synchronous mirror — avoids stale-closure races between
  // rapid clicks and the mouse-move preview handler while a POST for the previous segment is in flight
  const setWallDraftSynced = (val) => { wallDraftRef.current = val; setWallDraft(val); };
  const lastWallClickAtRef = useRef(0);
  const [selectedWallId, setSelectedWallId] = useState(null);
  const [templateDraft, setTemplateDraft] = useState(null); // { shape, start } while drag-placing a cone/line
  const [triggerLineDraft, setTriggerLineDraft] = useState(null); // { points: [{x,y}], preview: {x,y} } while chain-drawing a trigger line
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const clearDrafts = () => {
    setRuler(null);
    setWallDraftSynced(null);
    setTriggerLineDraft(null);
  };

  const switchTool = (toolId) => {
    setActiveTool((prev) => (prev === toolId ? null : toolId));
    clearDrafts();
  };

  const clearTool = () => {
    setActiveTool(null);
    clearDrafts();
  };

  // Konva's getPointerPosition() only returns raw screen-relative pixels — it ignores the
  // Stage's own pan (x/y from dragging) and zoom (scale) transform. getRelativePointerPosition()
  // is the correct Konva API here: it walks the Stage's transform matrix and returns the pointer
  // in the Stage's *local/untransformed* coordinate space, which is what every drawing tool
  // (walls, ruler, templates, obstacles) stores in the database. This fixes the "wall lands
  // where I clicked before panning/zooming" offset bug.
  const stagePointerPos = (stage) => {
    const pos = stage.getRelativePointerPosition();
    return { x: pos.x, y: pos.y };
  };

  // Shared between the Stage's own empty-space clicks AND clicking directly on an
  // already-placed wall segment while actively drawing (so ending the chain by clicking back
  // near/on the last placed point works, instead of that click being swallowed by the wall's
  // own onClick selection handler).
  const placeWallPoint = async (pos) => {
    const now = Date.now();
    if (now - lastWallClickAtRef.current < 180) return; // guards against duplicate click/dblclick synthetic events firing on top of each other
    lastWallClickAtRef.current = now;
    const draft = wallDraftRef.current;
    if (!draft) {
      setWallDraftSynced({ start: pos, end: pos });
      return;
    }
    // Chained wall drawing: keep placing connected segments from the last endpoint until the
    // GM clicks back on (or double-clicks) roughly the same spot to end the run.
    const distPx = Math.hypot(pos.x - draft.start.x, pos.y - draft.start.y);
    if (distPx < 6) {
      setWallDraftSynced(null);
      setActiveTool(null);
      return;
    }
    setWallDraftSynced({ start: pos, end: pos }); // advance immediately so a rapid next click can't reuse this segment's start
    try {
      await api.post(`/games/${game.id}/walls`, { scene_id: scene.id, x1: draft.start.x, y1: draft.start.y, x2: pos.x, y2: pos.y });
    } catch {
      toast.error("Couldn't place that wall segment — try again");
      setWallDraftSynced(null);
      setActiveTool(null);
    }
  };

  const runToolClickAtPos = async (pos) => {
    if (activeTool === "ruler") {
      if (!ruler) setRuler({ start: pos, end: pos });
      else {
        setRuler(null);
        setActiveTool(null);
      }
      return;
    }
    if (activeTool === "obstacle") {
      onOpenObstacleDialog({ pos });
      setActiveTool(null);
      return;
    }
    if (activeTool === "trigger-line") {
      if (!triggerLineDraft) {
        setTriggerLineDraft({ points: [pos], preview: pos });
      } else {
        const last = triggerLineDraft.points[triggerLineDraft.points.length - 1];
        const distToLast = Math.hypot(pos.x - last.x, pos.y - last.y);
        if (triggerLineDraft.points.length >= 2 && distToLast < 10) {
          onOpenObstacleDialog({ points: triggerLineDraft.points });
          setTriggerLineDraft(null);
          setActiveTool(null);
        } else {
          setTriggerLineDraft({ points: [...triggerLineDraft.points, pos], preview: pos });
        }
      }
      return;
    }
    if (activeTool === "door" || activeTool === "portal") {
      onOpenDoorDialog({ pos, isPortal: activeTool === "portal" });
      setActiveTool(null);
      return;
    }
    if (activeTool === "wall") {
      await placeWallPoint(pos);
      return;
    }
    if (activeTool === "cone" || activeTool === "line") {
      if (!templateDraft) {
        setTemplateDraft({ shape: activeTool, start: pos, end: pos });
      } else {
        const dx = pos.x - templateDraft.start.x;
        const dy = pos.y - templateDraft.start.y;
        const size = Math.max(20, Math.hypot(dx, dy));
        const rotation = (Math.atan2(dy, dx) * 180) / Math.PI;
        const sizeFt = Math.round((size / gridSize) * 5);
        try {
          await api.post(`/games/${game.id}/templates`, {
            scene_id: scene.id, shape: templateDraft.shape, x: templateDraft.start.x, y: templateDraft.start.y,
            rotation, size, color: "#D90429", label: `${sizeFt}ft`,
          });
        } catch {
          toast.error("Couldn't place that template — try again");
        } finally {
          setTemplateDraft(null);
          setActiveTool(null);
        }
      }
      return;
    }
    // circle stays a single click-to-place, its radius is adjustable afterward via the edit dialog
    const size = gridSize * 4;
    await api.post(`/games/${game.id}/templates`, {
      scene_id: scene.id, shape: "circle", x: pos.x, y: pos.y, size, color: "#D90429",
      label: `${Math.round((size / gridSize) * 5)}ft`,
    });
    setActiveTool(null);
  };

  const handleStageClick = async (e) => {
    const stage = e.target.getStage();
    const isEmptyClick = e.target === stage;
    if (arrivalPick) {
      if (isEmptyClick) onArrivalPick(stagePointerPos(stage));
      return;
    }
    if (activeTool && isEmptyClick) {
      await runToolClickAtPos(stagePointerPos(stage));
      return;
    }
    if (isEmptyClick) {
      setSelectedTemplateId(null);
      setSelectedWallId(null);
      onEmptyClick?.();
    }
  };

  // Whatever's directly under the cursor — a token, a lamp, an obstacle marker, a door, another
  // wall segment — shouldn't be able to "steal" a click away from an active drawing tool. A GM
  // laying down a wall/trigger-line/template needs to be able to click anywhere on the map,
  // including right on top of other objects, and have that placement land exactly where they
  // clicked. Every overlay element's onClick/onTap calls this FIRST; if it returns true (a tool
  // was active and consumed the click), the element's own normal action is skipped entirely.
  const handleOverlayClick = (e) => {
    if (!activeTool) return false;
    e.cancelBubble = true;
    runToolClickAtPos(stagePointerPos(e.target.getStage()));
    return true;
  };

  const handleStageMouseMove = (e) => {
    if (activeTool === "ruler" && ruler) {
      const stage = e.target.getStage();
      setRuler({ ...ruler, end: stagePointerPos(stage) });
    }
    if (activeTool === "wall" && wallDraft) {
      const stage = e.target.getStage();
      const pos = stagePointerPos(stage);
      // Functional update is REQUIRED here: this fires on every pixel of mouse movement, and a
      // stale closure capturing an old `wallDraft.start` (from before a just-resolved click's
      // setWallDraft) would otherwise clobber the chain's start point back to an earlier one.
      setWallDraft((prev) => (prev ? { ...prev, end: pos } : prev));
    }
    if (activeTool === "trigger-line" && triggerLineDraft) {
      const stage = e.target.getStage();
      const pos = stagePointerPos(stage);
      setTriggerLineDraft((prev) => (prev ? { ...prev, preview: pos } : prev));
    }
    if ((activeTool === "cone" || activeTool === "line") && templateDraft) {
      const stage = e.target.getStage();
      setTemplateDraft({ ...templateDraft, end: stagePointerPos(stage) });
    }
  };

  // Trigger lines can also be finished with an explicit double-click (in addition to clicking
  // back near the last placed point) — mirrors the "until double-click" ask literally, for runs
  // where the closing point isn't conveniently near the last one.
  const handleStageDblClick = () => {
    if (activeTool === "trigger-line" && triggerLineDraft && triggerLineDraft.points.length >= 2) {
      onOpenObstacleDialog({ points: triggerLineDraft.points });
      setTriggerLineDraft(null);
      setActiveTool(null);
    }
  };

  const rulerDistanceFt = ruler
    ? Math.round((Math.hypot(ruler.end.x - ruler.start.x, ruler.end.y - ruler.start.y) / gridSize) * 5)
    : 0;

  return {
    activeTool, switchTool, clearTool,
    ruler, wallDraft, templateDraft, triggerLineDraft,
    selectedWallId, setSelectedWallId,
    selectedTemplateId, setSelectedTemplateId,
    rulerDistanceFt,
    stagePointerPos,
    handleStageClick, handleStageMouseMove, handleStageDblClick, handleOverlayClick,
  };
}
